/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import toast from "react-hot-toast";

import { tryCatcher } from "hooks/useTryCatch";
import isObject from "utils/isObject";
import warning from "utils/warning";

import { toJSON } from "./Fetcher";
import { sentryCaptureError } from "utils/errorFormatter";
import { redirectToNoAccessPage } from "utils/eventSwitcher";

interface FetchErrorOptions {
  message: string;
  alertError: boolean;
  toastType: "error" | "info";
  shapeErrorMessage: ((error: any) => string) | null;
  redirectOnUnauthorized: boolean;
}

class FetchError {
  data: null;
  ok: boolean;
  status: "FAILED" | "INITIAL";
  statusCode: number | "UNKNOWN";
  error: string;
  response: Response | null;

  private alertError: FetchErrorOptions["alertError"];
  private toastType: FetchErrorOptions["toastType"];
  private message: FetchErrorOptions["message"];
  private shapeErrorMessage: FetchErrorOptions["shapeErrorMessage"];
  private redirectOnUnauthorized: FetchErrorOptions["redirectOnUnauthorized"];

  constructor(res: Response | null, options?: FetchErrorOptions | string) {
    const settings = {
      message: "",
      alertError: true,
      toastType: "error" as "error" | "info",
      shapeErrorMessage: null,
      redirectOnUnauthorized: true,
    };

    if (options) {
      if (typeof options === "string") settings.message = options;
      if (typeof options === "object") Object.assign(settings, options);
    }

    this.redirectOnUnauthorized = settings.redirectOnUnauthorized;
    this.alertError = settings.alertError;
    this.toastType = settings.toastType;
    this.message = settings.message;
    this.shapeErrorMessage = settings.shapeErrorMessage;

    if (this.message.length > 0) {
      if (this.toastType === "info") toast(this.message);
      else toast.error(this.message);
    }

    this.data = null;
    this.ok = false;
    this.status = "INITIAL";
    this.statusCode = 0;
    this.error = "";
    this.response = res;

    // @ts-ignore
    return Promise.resolve(this.init(res));
  }

  init = async (response: Response | null) => {
    // no response, means it is an offline custom error.
    if (!response) return this.processCustomError();

    let res;

    if (response.status === 401) {
      res = await this.process401Response(response);
    } else if (response.status === 422) {
      res = await this.process422Response(response);
    } else if (response.status === 404) {
      res = this.process404Response(response);
    } else if (response.status === 500) {
      res = this.process500Response(response);
    } else if (response.status === 403) {
      res = this.process403Response(response);
    }
    return res;
  };

  private async process401Response(res: Response) {
    let errorMessage = "";
    if (this.redirectOnUnauthorized) {
      errorMessage =
        this.message || "Please check your internet connection and try again.";
      localStorage.clear();
      // Adding a delay to ensure the toast message is displayed
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } else {
      const error = await toJSON(res);
      errorMessage = error;
      if (error.hasOwnProperty("error") || error.hasOwnProperty("errors")) {
        errorMessage =
          error.error ||
          (typeof error.errors === "string" ? error.errors : error.errors[0]);
        if (error.is_expired) {
          localStorage.clear();
          // Adding a delay to ensure the toast message is displayed
          setTimeout(() => {
            window.location.href = "/";
          }, 2000);
        }
      }
    }

    if (this.alertError) this.showErrorToast(errorMessage);

    this.data = null;
    this.ok = false;
    this.status = "FAILED";
    this.statusCode = res.status;
    this.error = errorMessage;
    this.response = res;
    sentryCaptureError(this, "Unauthorized Error in FetchError");

    return this;
  }

  private process404Response(res: Response) {
    const errorMessage = "Page Not Found";

    if (this.alertError) this.showErrorToast(errorMessage);

    this.data = null;
    this.ok = false;
    this.status = "FAILED";
    this.statusCode = res.status;
    this.error = errorMessage;
    this.response = res;
    sentryCaptureError(this, "Not Found Error in FetchError");
    redirectToNoAccessPage();

    return this;
  }

  private async process422Response(res: Response) {
    const payload = await toJSON(res);

    let errorKeyName = "error",
      errorMessage = "";

    if (isObject(payload)) {
      if (
        payload.hasOwnProperty("error") ||
        payload.hasOwnProperty("message")
      ) {
        errorKeyName = "error";
      } else {
        errorKeyName = Object.keys(payload)[0];
      }
    }

    if (Array.isArray(payload[errorKeyName])) {
      errorMessage = payload[errorKeyName][0];
    } else {
      errorMessage = payload[errorKeyName] || payload.message;
    }

    let toastMessage = errorMessage;
    if (
      errorKeyName &&
      errorKeyName !== "error" &&
      errorKeyName !== "message" &&
      typeof errorMessage === "string" &&
      errorMessage.length > 0
    ) {
      // Normalize the key name to be more readable
      const pretty = errorKeyName
        .replace(/[_-]+/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase());
      toastMessage = `${pretty} ${errorMessage}`.trim();
    }

    if (this.shapeErrorMessage) {
      toastMessage = this.shapeErrorMessage(toastMessage);
    }

    this.showErrorToast(toastMessage);

    this.data = null;
    this.ok = false;
    this.status = "FAILED";
    this.statusCode = res.status;
    this.error = errorMessage;
    this.response = res;
    sentryCaptureError(this, "Forbidden Error in FetchError");
    return this;
  }

  private process403Response(res: Response) {
    const errorMessage = "Event is canceled";

    localStorage.setItem("canceledEvent", "true");

    // Dispatch the custom event
    window.dispatchEvent(new CustomEvent("canceledEventSet"));

    this.data = null;
    this.ok = false;
    this.status = "FAILED";
    this.statusCode = 403;
    this.error = errorMessage;
    this.response = res;
    sentryCaptureError(this, "Internal Server Error in FetchError");
    redirectToNoAccessPage();

    return this;
  }

  private process500Response(res: Response) {
    const errorMessage = "Internal Server Error, please contact support.";
    if (this.alertError) this.showErrorToast(errorMessage);

    this.data = null;
    this.ok = false;
    this.status = "FAILED";
    this.statusCode = res.status;
    this.error = errorMessage;
    this.response = res;
    sentryCaptureError(this, "Other Error in FetchError");
    return this;
  }

  private processOtherResponse(res: Response) {
    warning(res);

    const errorMessage =
      "Something wrong happened, Please reach out to support.";
    if (this.alertError) this.showErrorToast(errorMessage);

    this.data = null;
    this.ok = false;
    this.status = "FAILED";
    this.statusCode = res.status || "UNKNOWN";
    this.error = errorMessage;
    this.response = res;

    sentryCaptureError(this, "Custom Error in FetchError");
    return this;
  }

  private processCustomError() {
    this.data = null;
    this.ok = false;
    this.status = "FAILED";
    this.statusCode = 400;
    this.error = "Bad request";
    this.response = null;

    sentryCaptureError(this, "Custom Error in FetchError");
    return this;
  }

  private showErrorToast = tryCatcher((msg: string) => {
    toast.dismiss();
    if (typeof msg !== "string") return;
    toast.error(msg);
  });
}

export default FetchError;
